import React, { useEffect, useMemo, useState } from 'react';
import { Link, Redirect, useHistory, useParams } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import AdminModal from './AdminModal';
import { useDashboardQuery } from '../apis/dashboard';
import { useDeleteGroupMutation, useUpdateGroupMutation } from '../apis/groups';
import {
  compareParentsByGroupAndName,
  getParentGroupNames,
  hasCompletedFacilitatorProgram,
  hasParentGroup,
  isParentInGroup,
  isSameId,
} from '../apis/normalizeDashboardData';
import { hasFullFacilitatorProgramAccessRole } from '../../../utils/authRole';

const getFullName = ({ firstName, lastName }) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const isNamedFacilitator = (facilitator) => {
  const name = getFullName(facilitator).toLowerCase();
  return Boolean(name) && name !== 'unassigned';
};

const buildGroupDetail = (routeGroupId, dashboardData) => {
  if (!dashboardData) {
    return null;
  }

  const selectedGroup =
    dashboardData.groups.find((group) => group.groupId === routeGroupId) ??
    null;

  if (!selectedGroup) {
    return null;
  }

  return {
    orgId: dashboardData.orgId,
    groupId: selectedGroup.groupId,
    groupName: selectedGroup.groupName,
    facilitators: selectedGroup.facilitators ?? [],
    parents: dashboardData.parents.filter((parent) =>
      selectedGroup.parentIds.some((parentId) =>
        isSameId(parentId, parent.userId),
      ),
    ),
  };
};

const EditGroupInfoPage = () => {
  const { groupId } = useParams();
  const history = useHistory();
  const canEditGroups = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  );
  const { data: dashboardData, isLoading, isError } = useDashboardQuery();
  const updateGroupMutation = useUpdateGroupMutation();
  const deleteGroupMutation = useDeleteGroupMutation();
  const isSavingGroup =
    updateGroupMutation.isPending || updateGroupMutation.isLoading;
  const isDeletingGroup =
    deleteGroupMutation.isPending || deleteGroupMutation.isLoading;

  const [groupTitle, setGroupTitle] = useState('');
  const [selectedFacilitators, setSelectedFacilitators] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [parentGroupFilter, setParentGroupFilter] = useState('all');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const groupDetail = useMemo(
    () => buildGroupDetail(groupId, dashboardData),
    [dashboardData, groupId],
  );

  useEffect(() => {
    if (!groupDetail) {
      return;
    }

    setGroupTitle(groupDetail.groupName);
    setSelectedFacilitators(
      groupDetail.facilitators
        .filter((facilitator) => hasCompletedFacilitatorProgram(facilitator))
        .map((facilitator) => facilitator.userId),
    );
    setSelectedParentIds(groupDetail.parents.map((parent) => parent.userId));
  }, [groupDetail]);

  const namedFacilitators = useMemo(
    () => (dashboardData?.facilitators ?? []).filter(isNamedFacilitator),
    [dashboardData?.facilitators],
  );
  const assignableFacilitators = useMemo(
    () =>
      namedFacilitators.filter((facilitator) =>
        hasCompletedFacilitatorProgram(facilitator),
      ),
    [namedFacilitators],
  );
  const assignableFacilitatorIds = useMemo(
    () =>
      new Set(assignableFacilitators.map((facilitator) => facilitator.userId)),
    [assignableFacilitators],
  );
  const ineligibleAssignedFacilitators = useMemo(
    () =>
      (groupDetail?.facilitators ?? []).filter(
        (facilitator) => !hasCompletedFacilitatorProgram(facilitator),
      ),
    [groupDetail?.facilitators],
  );

  const groupData = useMemo(() => {
    if (!groupDetail || !dashboardData) {
      return null;
    }

    return {
      ...groupDetail,
      groupName: groupTitle || groupDetail.groupName,
      facilitators: dashboardData.facilitators.filter((facilitator) =>
        selectedFacilitators.includes(facilitator.userId),
      ),
      parents: dashboardData.parents.filter((parent) =>
        selectedParentIds.includes(parent.userId),
      ),
    };
  }, [
    dashboardData,
    groupDetail,
    groupTitle,
    selectedFacilitators,
    selectedParentIds,
  ]);

  const filteredParents = useMemo(() => {
    if (!dashboardData) {
      return [];
    }

    return dashboardData.parents
      .filter((parent) => {
        if (parentGroupFilter === 'all') return true;
        if (parentGroupFilter === 'unassigned') return !hasParentGroup(parent);
        return isParentInGroup(parent, parentGroupFilter);
      })
      .sort((firstParent, secondParent) => {
        if (parentGroupFilter === 'all') {
          return compareParentsByGroupAndName(firstParent, secondParent);
        }

        return getFullName(firstParent).localeCompare(
          getFullName(secondParent),
        );
      });
  }, [dashboardData, parentGroupFilter]);

  const toggleFacilitator = (facilitatorId) => {
    setSelectedFacilitators((prev) =>
      prev.includes(facilitatorId)
        ? prev.filter((id) => id !== facilitatorId)
        : [...prev, facilitatorId],
    );
    setErrors((prev) => ({ ...prev, facilitatorIds: '' }));
  };

  const toggleParent = (parentId) => {
    setSelectedParentIds((prev) =>
      prev.includes(parentId)
        ? prev.filter((id) => id !== parentId)
        : [...prev, parentId],
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!groupData) {
      return;
    }

    const trimmedGroupTitle = groupTitle.trim();
    const validSelectedFacilitators = selectedFacilitators.filter((id) =>
      assignableFacilitatorIds.has(id),
    );

    if (!trimmedGroupTitle) {
      setErrors({ groupTitle: 'Group Name is required.' });
      return;
    }

    if (!validSelectedFacilitators.length) {
      setErrors((prev) => ({
        ...prev,
        facilitatorIds:
          'Assign at least one facilitator who has completed the program.',
      }));
      return;
    }

    try {
      await updateGroupMutation.mutateAsync({
        groupId,
        title: trimmedGroupTitle,
        facilitatorIds: validSelectedFacilitators,
        parentIds: selectedParentIds,
      });
      setErrors({});
      history.push(`/facilitator/dashboard/group/${groupId}`);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: error?.message || 'Unable to update group.',
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroupMutation.mutateAsync(groupId);
      setErrors((prev) => ({ ...prev, delete: '' }));
      setIsDeleteConfirmOpen(false);
      history.push('/facilitator/dashboard');
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        delete: error?.message || 'Unable to delete group.',
      }));
    }
  };

  if (!canEditGroups) {
    return <Redirect to={`/facilitator/dashboard/group/${groupId}`} />;
  }

  if (isLoading) {
    return (
      <div className='text-left'>
        <AdminHeader />
        <main className='mx-auto max-w-5xl p-4'>
          <div className='my-3'>
            <Link
              className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
              to='/facilitator/dashboard'
            >
              &larr; Go Back
            </Link>
          </div>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <h1 className='text-xl font-bold text-dark-blue'>
              Loading group info...
            </h1>
          </section>
        </main>
      </div>
    );
  }

  if (isError || !groupData) {
    return (
      <div className='text-left'>
        <AdminHeader />
        <main className='mx-auto max-w-5xl p-4'>
          <div className='my-3'>
            <Link
              className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
              to='/facilitator/dashboard'
            >
              &larr; Go Back
            </Link>
          </div>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <h1 className='text-xl font-bold text-red-600'>
              Error loading group data.
            </h1>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className='text-left'>
      <AdminHeader />
      <main className='p-4 max-w-5xl mx-auto'>
        <AdminModal
          title='Delete Group?'
          isOpen={isDeleteConfirmOpen}
          handleClose={() => {
            setIsDeleteConfirmOpen(false);
            setErrors((prev) => ({ ...prev, delete: '' }));
          }}
        >
          <div className='text-sm'>
            <p className='mb-3'>
              Are you sure you want to delete{' '}
              <span className='font-bold text-purple'>
                {groupData.groupName}
              </span>
              ?
            </p>
            <p className='mb-5 font-bold text-red-600'>
              This will remove the group from the dashboard and cannot be
              undone.
            </p>
            {errors.delete && (
              <p className='mb-5 font-bold text-red-700'>{errors.delete}</p>
            )}
            <div className='flex flex-wrap justify-end gap-3'>
              <button
                type='button'
                onClick={() => {
                  setIsDeleteConfirmOpen(false);
                  setErrors((prev) => ({ ...prev, delete: '' }));
                }}
                className='rounded border border-light-blue bg-white px-4 py-2 text-sm font-bold text-light-blue transition-colors duration-200 hover:bg-light-blue hover:text-white'
                disabled={isDeletingGroup}
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleDelete}
                className='rounded bg-red-600 px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70'
                disabled={isDeletingGroup}
              >
                {isDeletingGroup ? 'Deleting...' : 'Delete Group'}
              </button>
            </div>
          </div>
        </AdminModal>

        <div className='my-3'>
          <Link
            className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
            to={`/facilitator/dashboard/group/${groupId}`}
          >
            &larr; Go Back
          </Link>
        </div>

        <h1 className='my-5 text-3xl font-bold text-dark-blue'>
          Edit Group Info
        </h1>

        <form onSubmit={handleSubmit} className='flex flex-col gap-5 text-sm'>
          {errors.submit && (
            <section className='rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm'>
              <p className='font-bold text-red-700'>{errors.submit}</p>
            </section>
          )}
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 1
            </div>
            <h2 className='mb-3 border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue'>
              Edit Group Name
            </h2>
            <div className='mb-3 text-sm'>
              <span className='font-bold text-dark-blue'>Org ID:</span>{' '}
              {groupDetail.orgId}
            </div>
            <label
              className='block font-bold mb-1 text-dark-blue'
              htmlFor='groupTitle'
            >
              Group Name:
            </label>
            <input
              id='groupTitle'
              type='text'
              value={groupTitle}
              onChange={(event) => {
                setGroupTitle(event.target.value);
                setErrors((prev) => ({ ...prev, groupTitle: '' }));
              }}
              className='w-full max-w-md border border-gray-300 rounded px-3 py-2 text-sm'
            />
            {errors.groupTitle && (
              <p className='text-red-600 font-bold mt-1'>{errors.groupTitle}</p>
            )}
          </section>

          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 2
            </div>
            <h2 className='border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue mb-1'>
              Assign Facilitators to the Group
            </h2>
            <p className='mb-3 max-w-[75ch] text-sm leading-normal'>
              Check the boxes to update which facilitators are assigned to this
              group. Only facilitators who have completed <i>ez</i>Training are
              shown here.
            </p>
            {ineligibleAssignedFacilitators.length ? (
              <p className='mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900'>
                {ineligibleAssignedFacilitators
                  .map((facilitator) => getFullName(facilitator))
                  .filter(Boolean)
                  .join(', ')}{' '}
                {ineligibleAssignedFacilitators.length === 1 ? 'has' : 'have'}{' '}
                not completed the program, so{' '}
                {ineligibleAssignedFacilitators.length === 1
                  ? 'this facilitator is'
                  : 'these facilitators are'}{' '}
                not assignable here.
              </p>
            ) : null}
            {errors.facilitatorIds ? (
              <p className='mb-3 text-red-600 font-bold'>
                {errors.facilitatorIds}
              </p>
            ) : null}

            <div className='flex max-w-2xl flex-col gap-2'>
              {assignableFacilitators.length ? (
                assignableFacilitators.map((facilitator) => (
                  <label
                    key={facilitator.userId}
                    className='flex items-center gap-2 border border-gray-200 bg-white rounded p-3'
                  >
                    <input
                      type='checkbox'
                      className='accent-blue-600'
                      checked={selectedFacilitators.includes(
                        facilitator.userId,
                      )}
                      onChange={() => toggleFacilitator(facilitator.userId)}
                    />
                    <span className='font-bold text-sm'>
                      {getFullName(facilitator)}
                    </span>
                  </label>
                ))
              ) : (
                <p>
                  No facilitators who have completed the program are available.
                </p>
              )}
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 bg-blue-50/60 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 3
            </div>
            <h2 className='border-l-4 border-light-blue pl-3 text-xl font-bold text-dark-blue mb-1'>
              Assign Parents To The Group
            </h2>
            <p className='mb-3 max-w-[75ch] text-sm leading-normal'>
              Parents can only be in one group at a time. Selecting parents who
              are already in another group will move them into this group when
              changes are saved.
            </p>

            <label className='block font-bold mb-1' htmlFor='parentGroupFilter'>
              Filter by group
            </label>
            <select
              id='parentGroupFilter'
              value={parentGroupFilter}
              onChange={(event) => setParentGroupFilter(event.target.value)}
              className='border border-gray-300 rounded px-2.5 py-1.5 mb-4 text-sm'
            >
              <option value='all'>All Groups</option>
              <option value='unassigned'>Unassigned</option>
              {dashboardData.groups.map((currentGroup) => (
                <option key={currentGroup.groupId} value={currentGroup.groupId}>
                  {currentGroup.groupName}
                </option>
              ))}
            </select>

            <div className='grid max-w-3xl grid-cols-[minmax(180px,1fr)_minmax(140px,200px)] gap-3 items-start rounded-t border border-gray-200 bg-white px-4 py-3.5 font-bold'>
              <div>Parent Name</div>
              <div>Current Group</div>
            </div>

            {filteredParents.length ? (
              filteredParents.map((parent) => {
                const parentGroupNames = getParentGroupNames(parent);

                return (
                  <label
                    key={parent.userId}
                    className='grid max-w-3xl grid-cols-[minmax(180px,1fr)_minmax(140px,200px)] gap-3 items-start border-x border-b border-gray-200 bg-white px-4 py-4'
                  >
                    <span className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        className='accent-blue-600'
                        checked={selectedParentIds.includes(parent.userId)}
                        onChange={() => toggleParent(parent.userId)}
                      />
                      <span className='block font-bold'>
                        {getFullName(parent)}
                      </span>
                    </span>
                    <span>
                      {parentGroupNames.length ? (
                        parentGroupNames.join(', ')
                      ) : (
                        <span className='text-red-600 font-bold'>
                          Unassigned
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            ) : (
              <p className='py-3 font-bold text-red-600'>
                No parents match this filter.
              </p>
            )}
          </section>

          <div className='flex flex-wrap items-center justify-between gap-3'>
            <button
              type='button'
              onClick={() => setIsDeleteConfirmOpen(true)}
              className='rounded bg-red-600 px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-red-700'
            >
              Delete Group
            </button>
            <button
              type='submit'
              className='rounded bg-purple px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70'
              disabled={isSavingGroup}
            >
              {isSavingGroup ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EditGroupInfoPage;
